import { useRef, useState } from 'react'
import { useDropzone } from 'react-dropzone'
import toast from 'react-hot-toast'

import CameraOutlinedIcon from '@ant-design/icons/CameraOutlined'

import Button from '@mui/material/Button'
import Dialog from '@mui/material/Dialog'
import DialogActions from '@mui/material/DialogActions'
import DialogContent from '@mui/material/DialogContent'
import Stack from '@mui/material/Stack'
import { styled } from '@mui/material/styles'
import DialogTitle from '@components/_mui/DialogTitle'
import Typography from '@components/_mui/Typography'

import useDialog from '@shared/hooks/src/useDialog'
import usePromiseLoading from '@shared/hooks/src/usePromiseLoading'
import { toBase64 } from '@shared/utils'

import AvatarView from '@components/Avatar'
import Confirmation from '@components/Dialog/Confirmation'
import ImageCropStatic from '@components/ImageCrop'

import { DropzoneWrapper, PlaceholderWrapper, RootWrapper } from './AvatarUploader.utils'

/**
 * Avatar component that displays a user's profile picture and allows them to upload a new one.
 * Image can be cropped before upload.
 *
 * @example
 * <Avatar
 *   user={me}
 *   onChange={(image) => new Promise((resolve) => setTimeout(resolve, 1000))}
 *   onRemove={() => new Promise((resolve) => setTimeout(resolve, 1000))}
 * />
 *
 * @param user - The user object to display the profile picture for
 * @param readOnly - If true, the user cannot upload a new image
 * @param onChange - Callback function to handle when a new image is uploaded
 * @param onRemove - Callback function to handle when the image is removed
 */
export default function AvatarUploader({ user, readOnly = false, onChange, onRemove }) {
  const [image, setImage] = useState(null)
  const hasPhoto = user?.profilePictureThumbUrl

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    multiple: false,
    disabled: readOnly,
    maxFiles: 1,
    accept: {
      'image/*': [],
    },
    onDropAccepted: async (images) => {
      const base64Image = await toBase64(images[0])
      setImage(base64Image)
    },
    onDropRejected: () => toast.error('File must be an image'),
  })

  const confirmRemove = useDialog({
    component: Confirmation,
    props: ({ close }) => ({
      title: 'Remove Profile Picture',
      description: 'Are you sure you want to remove the profile picture?',
      rejectLabel: 'Cancel',
      confirmLabel: 'Remove',
      onReject: () => close(),
      onConfirm: () => onRemove().then(() => close()),
    }),
  })

  return (
    <Stack sx={{ alignItems: 'center' }}>
      <CropDialog file={image} open={Boolean(image)} onClose={() => setImage(null)} onSave={(image) => onChange(image)} />
      <RootWrapper>
        <DropzoneWrapper {...getRootProps()} sx={{ ...(isDragActive && { opacity: 0.6 }) }}>
          <AvatarView hideStatus variant="rounded" user={user} size="xxl" />
          <input {...getInputProps()} />
          {!readOnly && (
            <PlaceholderWrapper className="placeholder">
              <Stack sx={{ alignItems: 'center' }}>
                <CameraIcon />
                <Typography sx={{ color: 'text.contrast' }}>{hasPhoto ? 'Update' : 'Upload'}</Typography>
              </Stack>
            </PlaceholderWrapper>
          )}
        </DropzoneWrapper>
      </RootWrapper>
      {!readOnly && onRemove && hasPhoto && (
        <Button onClick={confirmRemove} color="secondary" size="small" sx={{ py: 0 }}>
          Remove
        </Button>
      )}
    </Stack>
  )
}

function CropDialog({ file, open, onClose, onSave }) {
  const cropperRef = useRef()

  const getCroppedImg = () => cropperRef.current?.cropper.getCroppedCanvas().toDataURL('image/jpeg')

  const [handleSave, loading] = usePromiseLoading(() => {
    const img = getCroppedImg()
    if (!img) return toast.error('There was an issue. Please try again.')
    return onSave(img).then(() => onClose())
  })

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="xs">
      <DialogTitle>Profile Image</DialogTitle>
      <DialogContent dividers>
        <ImageCropStatic ref={cropperRef} cropType="square" file={file} />
      </DialogContent>
      <DialogActions>
        <Button color="error" onClick={onClose} disabled={loading}>
          Cancel
        </Button>
        <Button variant="contained" loading={loading} onClick={handleSave}>
          Save
        </Button>
      </DialogActions>
    </Dialog>
  )
}

const CameraIcon = styled(CameraOutlinedIcon)(({ theme }) => ({
  color: theme.palette.text.contrast,
  fontSize: '2rem',
}))
